import { GoogleMapsOverlay as DeckOverlay } from '@deck.gl/google-maps';
import {
  GOOGLE_MAP_ID_LIGHT_3D,
  GOOGLE_MAP_ID_DARK_3D,
  GOOGLE_MAP_ID_DARK_2D,
  GOOGLE_MAP_ID_LIGHT_2D,
  GOOGLE_MAP_ID_DRAWING,
} from '@env';
import { Observable, Subject } from 'rxjs';

import { CdkPortalOutlet } from '@angular/cdk/portal';
import { Injector, ViewContainerRef, NgZone } from '@angular/core';

import { IFsMap } from '@lib/geo/core';
import {
  SnLonLat,
  SnLonLatLike,
  SnLonLatBoundsLike,
  SnLonLatBounds,
  ShapeStylesType,
  FsMapControlsModes,
} from '@lib/geo/helpers';
import { Maps } from '@lib/geo/helpers';

import { Evented } from '../core';
import { GoogleMapStyle } from '../enums';
import {
  toGoogleMapLatLng,
  toGoogleMapTypeId,
  toGoogleMapOptions,
  fromGoogleMapLatLng,
  fromGoogleMapTypeId,
  toGoogleMapLatLngBounds,
  fromGoogleMapLatLngBounds,
} from '../helpers';
import { GoogleMapLayer } from '../overlays';
import { GoogleMapControls } from './google-map-controls';
import { IGoogleMapEvents } from './google-map-events';
import { IGoogleMapOptions } from './google-map-options';
import { GoogleMapOverlays } from './google-map-overlays';

export class GoogleMap extends Evented<IGoogleMapEvents> implements IFsMap {
  public overlays: GoogleMapOverlays;
  public layers: GoogleMapLayer = new GoogleMapLayer();
  public controls: GoogleMapControls;
  public mapIs3D: boolean = false;
  public webGLOverlays = {};
  public isDrawing: boolean = false;
  public options: IGoogleMapOptions;

  public readonly zone: NgZone;
  public readonly portal: CdkPortalOutlet;
  public readonly injector: Injector;
  public readonly viewContainerRef: ViewContainerRef;
  public readonly geospatialProvider = Maps.GOOGLE;
  public readonly id: string = 'google-map';
  // geospatialProvider, getZoom, setZoom, getCenter
  private container: HTMLElement;
  private _googleMap: google.maps.Map;
  private markers: google.maps.marker.AdvancedMarkerElement[] = [];
  private _hoveredLayers = [];
  private readonly _eventListenners: google.maps.MapsEventListener[] = [];

  private readonly _newShape$: Subject<any> = new Subject();
  private readonly _startDrawing$: Subject<any> = new Subject();
  private readonly _destroy$: Subject<void> = new Subject();
  private _tilesLoadTimeout: number | undefined;
  private _tilesLoadTimeoutMs: number = 12000;
  constructor(
    container: HTMLElement,
    options: IGoogleMapOptions,
    portal: CdkPortalOutlet,
    zone: NgZone,
    injector: Injector,
    viewContainerRef: ViewContainerRef,
  ) {
    super();
    let mapID: string;
    switch (options.style) {
      case 'light':
        mapID = GOOGLE_MAP_ID_LIGHT_2D;
        this.mapIs3D = false;
        break;
      case 'dark':
        mapID = GOOGLE_MAP_ID_DARK_2D;
        this.mapIs3D = false;
        break;
      case 'light3d':
        mapID = GOOGLE_MAP_ID_LIGHT_3D;
        this.mapIs3D = true;
        break;
      case 'dark3d':
        mapID = GOOGLE_MAP_ID_DARK_3D;
        this.mapIs3D = true;
        break;
      case 'drawing':
        mapID = GOOGLE_MAP_ID_DRAWING;
        this.mapIs3D = false;
        break;
      case 'satellite':
        mapID = google.maps.MapTypeId.HYBRID;
        break;
      case 'terrain':
        mapID = google.maps.MapTypeId.TERRAIN;
        break;
      default:
        mapID = GOOGLE_MAP_ID_DARK_2D;
        this.mapIs3D = false;
        options.style = GoogleMapStyle.Dark;
        break;
    }
    this._googleMap = this._createGoogleMap(container, toGoogleMapOptions(options), mapID);
    this.container = container;
    this.options = options;

    this.overlays = new GoogleMapOverlays(this);
    this.controls = new GoogleMapControls(this);

    this.zone = zone;
    this.portal = portal;
    this.injector = injector;
    this.viewContainerRef = viewContainerRef;

    this._initEventListenners();
    this._setupTilesLoadMonitoring();
  }

  setMapId(mapId: string): void {
    this.setNewMapID(mapId);
  }

  addLayer(layer: any): void {
    layer.setMap(this.mapInstance);
  }

  removeLayer(layer: any): void {
    layer.setMap(null);
  }

  removeLayers(): void {
    this.overlays?.clear();
  }

  get mapInstance(): google.maps.Map {
    return this._googleMap;
  }

  get newShape$(): Observable<any> {
    return this._newShape$.asObservable();
  }

  get startDrawing$(): Observable<any> {
    return this._startDrawing$.asObservable();
  }

  public addMarker(marker: google.maps.marker.AdvancedMarkerElement): void {
    this.markers.push(marker);
  }

  public removeMarker(marker: google.maps.marker.AdvancedMarkerElement): void {
    marker.map = null;
    this.markers = this.markers.filter((m) => m !== marker);
  }

  public getBounds(): SnLonLatBounds {
    return fromGoogleMapLatLngBounds(this._googleMap.getBounds());
  }

  public getCenter(): SnLonLat {
    return fromGoogleMapLatLng(this._googleMap.getCenter());
  }

  public getZoom(): number {
    return this._googleMap.getZoom();
  }

  public getHeading(): number {
    return this._googleMap.getHeading();
  }

  public getWebGLOverlays(): any {
    return this.webGLOverlays;
  }

  public getStyle(): GoogleMapStyle {
    if (this._googleMap.getMapTypeId() === 'roadmap') {
      return fromGoogleMapTypeId(this.options.style);
    }
    return fromGoogleMapTypeId(this._googleMap.getMapTypeId());
  }

  public setWebGLOverlays(overlay: DeckOverlay, id: string): this {
    if (!this.webGLOverlays[id]) {
      this.webGLOverlays[id] = [];
    }
    const index = this.webGLOverlays[id].findIndex((o) => o.id === overlay.props.id);
    if (index > -1) {
      this.webGLOverlays[id][index] = overlay;
    } else {
      this.webGLOverlays[id].push(overlay);
    }
    return this;
  }

  public getContainer(): HTMLElement {
    return this.container;
  }

  public setCenter(lonLatLike: SnLonLatLike): this {
    const lonLat = SnLonLat.convert(lonLatLike);
    this._googleMap.setCenter(toGoogleMapLatLng(lonLat));

    return this;
  }

  public setHoveredLayers(layer: string, hovered: boolean): void {
    if (hovered) {
      this._hoveredLayers.push(layer);
    } else {
      this._hoveredLayers = this._hoveredLayers.filter((l) => l !== layer);
    }
    if (this.isDrawing) {
      this._googleMap.setOptions({
        draggableCursor: 'crosshair',
      });
    } else {
      this._googleMap.setOptions({
        draggableCursor: this._hoveredLayers.length > 0 ? 'pointer' : 'grab',
      });
    }
  }

  public turnOff3D(): void {
    if (this.mapIs3D) {
      this._googleMap = this._createGoogleMap(
        this.container,
        toGoogleMapOptions(this.options),
        GOOGLE_MAP_ID_LIGHT_2D,
      );
      this.overlays = new GoogleMapOverlays(this);
      this.controls = new GoogleMapControls(this);
      this._initEventListenners();
      this.mapIs3D = false;
    }
  }

  public setStyle(style: string): this {
    const formattedStyle = this._formatStyle(style);
    this.setNewMapID(formattedStyle);
    return this;
  }

  public setZoom(zoom: number): this {
    this._googleMap.setZoom(zoom);

    return this;
  }

  public setOptions(options?: IGoogleMapOptions): this {
    this._googleMap.setOptions(toGoogleMapOptions(options));

    return this;
  }

  public setStreetView(streetView: any): this {
    this._googleMap.setStreetView(streetView);

    return this;
  }

  public goTo(lonLatLike: SnLonLatLike): this {
    const lonLat = SnLonLat.convert(lonLatLike);
    this._googleMap.panTo(toGoogleMapLatLng(lonLat));

    return this;
  }

  public panTo(lonLatLike: SnLonLatLike): this {
    const lonLat = SnLonLat.convert(lonLatLike);
    this._googleMap.panTo(toGoogleMapLatLng(lonLat));

    return this;
  }

  public setTilt(tilt: number): this {
    this._googleMap.setTilt(tilt);

    return this;
  }

  public setHeading(heading: number): this {
    this._googleMap.setHeading(heading);

    return this;
  }

  public contains(lonLatLike: SnLonLatLike): boolean {
    const lonLat = SnLonLat.convert(lonLatLike);
    return this._googleMap?.getBounds()?.contains(toGoogleMapLatLng(lonLat));
  }

  public panToBounds(lonLatBoundsLike: SnLonLatBoundsLike): this {
    const lonLatBounds = SnLonLatBounds.convert(lonLatBoundsLike);
    this._googleMap.fitBounds(toGoogleMapLatLngBounds(lonLatBounds));

    return this;
  }

  public fitBounds(bounds: SnLonLatBounds | google.maps.LatLngBounds): this {
    if (bounds instanceof SnLonLatBounds) {
      const lonLatBounds = SnLonLatBounds.convertToLatLng(bounds);
      this._googleMap.fitBounds(lonLatBounds);
    } else {
      this._googleMap.fitBounds(bounds);
    }
    return this;
  }

  public destroy(): void {
    this._stopTilesLoadMonitoring();
    this._destroyEventListenners();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public disableZoom(): void {
    this._googleMap.setOptions({
      zoomControl: false,
    });
  }

  public emitDrawing(
    tool: 'circle' | 'point' | 'polygon' | 'polyline' | 'rectangle',
    mode: FsMapControlsModes,
    style?: ShapeStylesType,
  ): void {
    this._startDrawing$.next({ tool, mode, style });
  }

  public freehand(): void {
    this._startDrawing$.next({ tool: 'pen' });
  }

  public newShape(shape: any): void {
    this._newShape$.next(shape);
  }

  public addShape(shape: any): void {
    shape.setMap(this.mapInstance);
  }

  public removeShape(shape: any): void {
    shape.setMap(null);
  }

  public addImageOverlay(overlay: any): void {
    overlay.setMap(this.mapInstance);
  }

  public removeImageLayer(overlay: any): void {
    overlay.setMap(null);
  }

  public setNewMapID(mapStyle: string, zoom?: number): google.maps.Map {
    let mapID: string;
    switch (mapStyle) {
      case 'light':
        mapID = GOOGLE_MAP_ID_LIGHT_2D;
        this.options.tilt = 0;
        this.options.style = GoogleMapStyle.Light;
        this.mapIs3D = false;
        break;
      case 'dark':
        mapID = GOOGLE_MAP_ID_DARK_2D;
        this.options.tilt = 0;
        this.options.style = GoogleMapStyle.Dark;
        this.mapIs3D = false;
        break;
      case 'light3d':
        mapID = GOOGLE_MAP_ID_LIGHT_3D;
        this.options.tilt = 67.5;
        this.options.style = GoogleMapStyle.Light3D;
        this.mapIs3D = true;
        break;
      case 'dark3d':
        mapID = GOOGLE_MAP_ID_DARK_3D;
        this.options.tilt = 67.5;
        this.options.style = GoogleMapStyle.Dark3D;
        this.mapIs3D = true;
        break;
      case 'drawing':
        mapID = GOOGLE_MAP_ID_DRAWING;
        this.options.style = GoogleMapStyle.Drawing;
        this.mapIs3D = false;
        break;
      case 'satellite':
        mapID = google.maps.MapTypeId.HYBRID;
        this.options.tilt = 0;
        this.options.style = GoogleMapStyle.Satellite;
        break;
      case 'terrain':
        mapID = google.maps.MapTypeId.TERRAIN;
        this.options.tilt = 0;
        this.options.style = GoogleMapStyle.TERRAIN;
        break;
      default:
        mapID = GOOGLE_MAP_ID_DARK_2D;
        this.options.tilt = 0;
        this.options.style = GoogleMapStyle.Dark;
        this.mapIs3D = false;
        break;
    }

    this.options.zoom = zoom || this.getZoom() || 18;
    this.options.mapId = mapID;
    this.options.center = this.getCenter();
    this.options.backgroundColor = 'none';
    this.options.noClear = true;
    this.options.maxZoom = 20;
    this._googleMap = this._createGoogleMap(
      this.container,
      toGoogleMapOptions(this.options),
      mapID,
    );
    this.controls = new GoogleMapControls(this);
    this.overlays = new GoogleMapOverlays(this);

    this._initEventListenners();
    if (mapStyle === 'satellite') {
      this._googleMap.setMapTypeId(toGoogleMapTypeId(GoogleMapStyle.Satellite));
    }
    if (mapStyle === 'terrain') {
      this._googleMap.setMapTypeId(toGoogleMapTypeId(GoogleMapStyle.TERRAIN));
    }
    this.fire('styleChange');
    return this._googleMap;
  }

  private _calculateTilt(customMapId: string, tilt: number): number {
    if (customMapId === (google.maps.MapTypeId.HYBRID || google.maps.MapTypeId.TERRAIN)) {
      return 0;
    } else if (tilt) {
      return tilt;
    } else {
      return null;
    }
  }

  private _initEventListenners(): void {
    const eventListenners = [
      this._googleMap.addListener('click', this._onClick.bind(this)),
      this._googleMap.addListener('dblclick', this._onDblClick.bind(this)),
      this._googleMap.addListener('rightclick', this._onRightClick.bind(this)),
      this._googleMap.addListener('dragstart', this._onDragStart.bind(this)),
      this._googleMap.addListener('drag', this._onDrag.bind(this)),
      this._googleMap.addListener('dragend', this._onDragEnd.bind(this)),
      this._googleMap.addListener('center_changed', this._onCenterChanged.bind(this)),
      this._googleMap.addListener('bounds_changed', this._onBoundsChanged.bind(this)),
      this._googleMap.addListener('zoom_changed', this._onZoomChanged.bind(this)),
      this._googleMap.addListener('idle', this._onIdle.bind(this)),
    ];

    this._eventListenners.push(...eventListenners);
  }

  private _setupTilesLoadMonitoring(): void {
    this._tilesLoadTimeout = window.setTimeout(() => {
      this.fire('tilesLoadError', { timeoutMs: this._tilesLoadTimeoutMs });
    }, this._tilesLoadTimeoutMs);

    const onTilesLoaded = this._googleMap.addListener('tilesloaded', () => {
      if (this._tilesLoadTimeout) {
        window.clearTimeout(this._tilesLoadTimeout);
        this._tilesLoadTimeout = undefined;
      }
      this.fire('tilesLoaded');
      onTilesLoaded.remove();
    });
  }

  private _stopTilesLoadMonitoring(): void {
    if (this._tilesLoadTimeout) {
      window.clearTimeout(this._tilesLoadTimeout);
      this._tilesLoadTimeout = undefined;
    }
  }

  private _destroyEventListenners(): void {
    this._eventListenners.forEach((event) => event?.remove());
  }

  private _onClick(googleEvent: google.maps.MapMouseEvent): void {
    const lonLat = fromGoogleMapLatLng(googleEvent.latLng);
    this.fire('click', lonLat);
  }

  private _onDblClick(googleEvent: google.maps.MapMouseEvent): void {
    const lonLat = fromGoogleMapLatLng(googleEvent.latLng);
    this.fire('dblclick', lonLat);
  }

  private _onRightClick(googleEvent: google.maps.MapMouseEvent): void {
    const lonLat = fromGoogleMapLatLng(googleEvent.latLng);
    this.fire('rightclick', lonLat);
  }

  private _onDragStart(): void {
    this.fire('dragstart');
  }

  private _onDrag(): void {
    this.fire('drag');
  }

  private _onDragEnd(): void {
    this.fire('dragend');
  }

  private _onIdle(): void {
    this.fire('idle');
  }

  private _onCenterChanged(): void {
    this.fire('centerChange');
  }

  private _onBoundsChanged(): void {
    this.fire('boundsChange');
  }

  private _onZoomChanged(): void {
    this.fire('zoomChange', this.getZoom());
  }

  private _createGoogleMap(
    container: HTMLElement,
    options: google.maps.MapOptions,
    customMapId?: string,
  ): google.maps.Map {
    if (this._googleMap) {
      this.fire('destroyed');
      setTimeout(() => {}, 1000);
    }
    return new google.maps.Map(container, {
      mapId: customMapId,
      zoom: options.zoom,
      center: options.center,
      backgroundColor: options.backgroundColor,
      isFractionalZoomEnabled: false,
      clickableIcons: false,
      panControl: false,
      zoomControl: false,
      cameraControl: false,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP,
      },
      tilt: this._calculateTilt(customMapId, options.tilt),
      minZoom: 4,
      scaleControl: false,
      rotateControl: false,
      mapTypeControl: false,
      mapTypeControlOptions: {
        mapTypeIds: [
          GoogleMapStyle.Light,
          GoogleMapStyle.Dark,
          google.maps.MapTypeId.HYBRID,
          google.maps.MapTypeId.TERRAIN,
        ],
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      fullscreenControl: false,
      streetViewControl: false,
    });
  }

  private _formatStyle(style: string): GoogleMapStyle {
    switch (style.toLowerCase()) {
      case 'light':
        return GoogleMapStyle.Light;
      case 'dark':
        return GoogleMapStyle.Dark;
      case 'satellite':
        return GoogleMapStyle.Satellite;
      case 'drawing':
        return GoogleMapStyle.Drawing;
    }
  }
}
